import { CommonConstants } from "@/common/CommonConstants";
import {
  DriverWorkShiftEdit,
  DriverWorkShiftError,
} from "@/models/driverWorkShiftsAndCycle";
import {
  DriverWorkCyclesConstants,
  duplicateTime,
  getBin,
  getTime,
  validDescription,
  validName,
} from "@/pages/DriverWorkCycles/DriverWorkCyclesConstants";
import {
  workingHours,
  workingMins,
} from "@/pages/OperationPlans/OperationPlansConstansts";
import { Messages } from "@/util/Messages";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextField from "../atoms/TextField";
import { ActionButtons } from "../common/buttonGroup/ActionButtons";
import { Autocomplete } from "../molecules/AutoComplete";
import Title from "../molecules/Title";

export type DriverWorkShiftEditTemplateProps = {
  title:
    | typeof DriverWorkCyclesConstants.DriverWorkShiftEdit
    | typeof DriverWorkCyclesConstants.DriverWorkShiftCreate;
  driverWorkShift: DriverWorkShiftEdit;
  loading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onChangeData: (driverWorkShift: DriverWorkShiftEdit) => void;
};

export const DriverWorkShiftEditTemplate = ({
  title,
  driverWorkShift,
  loading,
  onCancel,
  onSubmit,
  onChangeData,
}: DriverWorkShiftEditTemplateProps) => {
  const forEdit = useMemo(
    () => title === DriverWorkCyclesConstants.DriverWorkShiftEdit,
    [title]
  );

  const [error, setError] = useState<DriverWorkShiftError>({});
  const [sameWorkingTime, setSameWorkingTime] = useState(!forEdit);

  const handleChangeDayOff = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        weekdayIncomingBin: _0,
        weekdayOutgoingBin: _1,
        weekdayStartHours: _2,
        weekdayStartMinutes: _3,
        weekdayEndHours: _4,
        weekdayEndMinutes: _5,
        holidayIncomingBin: _6,
        holidayOutgoingBin: _7,
        holidayStartHours: _8,
        holidayStartMinutes: _9,
        holidayEndHours: _10,
        holidayEndMinutes: _11,
        ...rest
      } = driverWorkShift;

      onChangeData({
        ...rest,
        weekdayIsDayOff: e.target.checked,
        holidayIsDayOff: e.target.checked,
      });
      setError({});
    },
    [driverWorkShift, onChangeData]
  );

  const generateTimeHandler = useCallback(
    (
      dayType: "weekday" | "holiday",
      period: "Start" | "End",
      scale: "Hours" | "Minutes"
    ) => {
      const target = period === "Start" ? "from" : "to";
      const withTime = scale === "Hours" ? "Minutes" : "Hours";
      const withValue = driverWorkShift[`${dayType}${period}${withTime}`];

      const targetKey = `${dayType}${period}${scale}`;
      const targetBin = `${dayType}${
        period === "Start" ? "Outgoing" : "Incoming"
      }Bin`;

      const duplicateErrorKey = `${dayType}IsDuplicate`;

      const reversedDayType = dayType === "weekday" ? "holiday" : "weekday";
      const reversedKey = `${reversedDayType}${period}${scale}`;
      const reversedBin = `${reversedDayType}${
        period === "Start" ? "Outgoing" : "Incoming"
      }Bin`;

      const getBinWrapper = (value?: string) => {
        return withTime === "Minutes"
          ? getBin(target, value, withValue)
          : getBin(target, withValue, value);
      };

      return (
        e: React.SyntheticEvent<Element, Event>,
        value: string | null | undefined
      ) => {
        setError({
          ...error,
          [targetKey]: false,
          [duplicateErrorKey]: false,
        });

        const bin = getBinWrapper(value ?? undefined);
        let nextData = {
          ...driverWorkShift,
          [targetBin]: bin,
          [targetKey]: value ?? undefined,
        };
        nextData[`${dayType}IsDayOff`] = duplicateTime(dayType, nextData);

        if (
          dayType === "weekday" &&
          sameWorkingTime &&
          !nextData[`${dayType}IsDayOff`]
        ) {
          nextData = {
            ...nextData,
            [reversedBin]: bin,
            [reversedKey]: value ?? undefined,
          };
        }

        // 片方だけ休みフラグが立っていたら、同じ時間フラグを外す
        setSameWorkingTime((prev) => prev && !nextData[`${dayType}IsDayOff`]);
        onChangeData(nextData);
      };
    },
    [driverWorkShift, error, onChangeData, sameWorkingTime]
  );

  const handleChangeWeekdayStartHours = useMemo(
    () => generateTimeHandler("weekday", "Start", "Hours"),
    [generateTimeHandler]
  );

  const handleChangeWeekdayStartMinutes = useMemo(
    () => generateTimeHandler("weekday", "Start", "Minutes"),
    [generateTimeHandler]
  );

  const handleChangeWeekdayEndHours = useMemo(
    () => generateTimeHandler("weekday", "End", "Hours"),
    [generateTimeHandler]
  );

  const handleChangeWeekdayEndMinutes = useMemo(
    () => generateTimeHandler("weekday", "End", "Minutes"),
    [generateTimeHandler]
  );

  const handleChangeHolidayStartHours = useMemo(
    () => generateTimeHandler("holiday", "Start", "Hours"),
    [generateTimeHandler]
  );

  const handleChangeHolidayStartMinutes = useMemo(
    () => generateTimeHandler("holiday", "Start", "Minutes"),
    [generateTimeHandler]
  );

  const handleChangeHolidayEndHours = useMemo(
    () => generateTimeHandler("holiday", "End", "Hours"),
    [generateTimeHandler]
  );

  const handleChangeHolidayEndMinutes = useMemo(
    () => generateTimeHandler("holiday", "End", "Minutes"),
    [generateTimeHandler]
  );

  const handleChangeSameWorkingTime = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSameWorkingTime(e.target.checked);
      if (!e.target.checked) {
        return;
      }

      const [holidayStartHours, holidayStartMinutes] = getTime(
        "from",
        driverWorkShift?.weekdayOutgoingBin
      );
      const [holidayEndHours, holidayEndMinutes] = getTime(
        "to",
        driverWorkShift?.weekdayIncomingBin
      );

      onChangeData({
        ...driverWorkShift,
        holidayOutgoingBin: driverWorkShift?.weekdayOutgoingBin,
        holidayIncomingBin: driverWorkShift?.weekdayIncomingBin,
        holidayStartHours,
        holidayStartMinutes,
        holidayEndHours,
        holidayEndMinutes,
      });
      setError({
        ...error,
        holidayStartHours: false,
        holidayStartMinutes: false,
        holidayEndHours: false,
        holidayEndMinutes: false,
      });
    },
    [driverWorkShift, error, onChangeData]
  );

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeData({
        ...driverWorkShift,
        shortName: e.target.value,
      });
      setError({
        ...error,
        shortName: false,
      });
    },
    [driverWorkShift, error, onChangeData]
  );

  const handleChangeDescription = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeData({
        ...driverWorkShift,
        description: e.target.value,
      });
      setError({
        ...error,
        description: false,
      });
    },
    [driverWorkShift, error, onChangeData]
  );

  const handleSubmit = useCallback(() => {
    const currentError: DriverWorkShiftError = {
      shortName:
        !driverWorkShift?.shortName?.length ||
        !validName(driverWorkShift.shortName),
      description: !driverWorkShift?.description?.length
        ? false
        : !validDescription(driverWorkShift.description),
      weekdayStartHours:
        !driverWorkShift?.weekdayIsDayOff &&
        !driverWorkShift?.weekdayStartHours?.length,
      weekdayStartMinutes:
        !driverWorkShift?.weekdayIsDayOff &&
        !driverWorkShift?.weekdayStartMinutes?.length,
      weekdayEndHours:
        !driverWorkShift?.weekdayIsDayOff &&
        !driverWorkShift?.weekdayEndHours?.length,
      weekdayEndMinutes:
        !driverWorkShift?.weekdayIsDayOff &&
        !driverWorkShift?.weekdayEndMinutes?.length,
      holidayStartHours:
        !driverWorkShift?.holidayIsDayOff &&
        !sameWorkingTime &&
        !driverWorkShift?.holidayStartHours?.length,
      holidayStartMinutes:
        !driverWorkShift?.holidayIsDayOff &&
        !sameWorkingTime &&
        !driverWorkShift?.holidayStartMinutes?.length,
      holidayEndHours:
        !driverWorkShift?.holidayIsDayOff &&
        !sameWorkingTime &&
        !driverWorkShift?.holidayEndHours?.length,
      holidayEndMinutes:
        !driverWorkShift?.holidayIsDayOff &&
        !sameWorkingTime &&
        !driverWorkShift?.holidayEndMinutes?.length,
    };
    if (Object.values(currentError).some((error) => error)) {
      setError(currentError);
      return;
    }

    onSubmit();
  }, [driverWorkShift, onSubmit, sameWorkingTime]);

  const flagInit = useRef(!forEdit);
  useEffect(() => {
    if (flagInit.current) {
      return;
    }

    flagInit.current = driverWorkShift.weekdayOutgoingBin != null;
    setSameWorkingTime(
      driverWorkShift.weekdayOutgoingBin != null &&
        driverWorkShift.weekdayIncomingBin != null &&
        driverWorkShift.holidayIncomingBin != null &&
        driverWorkShift.holidayOutgoingBin != null &&
        driverWorkShift.weekdayOutgoingBin ===
          driverWorkShift.holidayOutgoingBin &&
        driverWorkShift.weekdayIncomingBin ===
          driverWorkShift.holidayIncomingBin
    );
  }, [
    driverWorkShift.holidayIncomingBin,
    driverWorkShift.holidayOutgoingBin,
    driverWorkShift.weekdayIncomingBin,
    driverWorkShift.weekdayOutgoingBin,
  ]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{title}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column " }}>
              <Table
                size="small"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                  backgroundColor: "#FFF",
                  mb: 2,
                  width: "60%",
                }}
                className="gx-info-table-no-width"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CommonConstants.CompanyOffice}
                        value={driverWorkShift?.offices?.displayName ?? ""}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <TextField
                        label={DriverWorkCyclesConstants.DriverWorkShiftName}
                        value={driverWorkShift?.shortName ?? ""}
                        onChange={handleChangeName}
                        error={!!error.shortName}
                        errorLabel={
                          !driverWorkShift.shortName?.length
                            ? `${DriverWorkCyclesConstants.DriverWorkShifts}${Messages.PleaseEnter}`
                            : DriverWorkCyclesConstants.LengthError
                        }
                        sx={{
                          width: "100%",
                          wordBreak: "break-all",
                        }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={DriverWorkCyclesConstants.Description}
                        value={driverWorkShift?.description ?? ""}
                        onChange={handleChangeDescription}
                        error={!!error.description}
                        errorLabel={
                          !driverWorkShift.description?.length
                            ? `${DriverWorkCyclesConstants.Description}${Messages.PleaseEnter}`
                            : DriverWorkCyclesConstants.DescriptionError
                        }
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <FormControl>
                        <FormLabel component="legend" sx={{ fontSize: 1 }}>
                          {DriverWorkCyclesConstants.DayOff}
                        </FormLabel>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                style={{ fontSize: "10px" }}
                                checked={
                                  !!driverWorkShift.weekdayIsDayOff &&
                                  !!driverWorkShift.holidayIsDayOff
                                }
                                onChange={handleChangeDayOff}
                              />
                            }
                            label={DriverWorkCyclesConstants.DayOff}
                          />
                        </FormGroup>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={
                        !!driverWorkShift.weekdayIsDayOff &&
                        !!driverWorkShift.holidayIsDayOff
                          ? undefined
                          : "required"
                      }
                    >
                      <Grid item>
                        <FormControl>
                          <FormLabel sx={{ fontSize: "0.75rem" }}>
                            {`${DriverWorkCyclesConstants.WeekDay} : ${DriverWorkCyclesConstants.StartTime}`}
                          </FormLabel>
                          <Grid container item>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingHours}
                              value={driverWorkShift.weekdayStartHours}
                              onChange={handleChangeWeekdayStartHours}
                              defaultValue={undefined}
                              error={!!error.weekdayStartHours}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                !!driverWorkShift.weekdayIsDayOff &&
                                !!driverWorkShift.holidayIsDayOff
                              }
                            />
                            <Typography
                              sx={{ alignSelf: "center" }}
                              component={"span"}
                            >
                              &nbsp;:&nbsp;
                            </Typography>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingMins}
                              value={driverWorkShift.weekdayStartMinutes}
                              onChange={handleChangeWeekdayStartMinutes}
                              defaultValue={undefined}
                              error={!!error.weekdayStartMinutes}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                !!driverWorkShift.weekdayIsDayOff &&
                                !!driverWorkShift.holidayIsDayOff
                              }
                            />
                          </Grid>
                        </FormControl>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={
                        !!driverWorkShift.weekdayIsDayOff &&
                        !!driverWorkShift.holidayIsDayOff
                          ? undefined
                          : "required"
                      }
                    >
                      <Grid item>
                        <FormControl>
                          <FormLabel sx={{ fontSize: "0.75rem" }}>
                            {`${DriverWorkCyclesConstants.WeekDay} : ${DriverWorkCyclesConstants.EndTime}`}
                          </FormLabel>
                          <Grid container item>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingHours}
                              value={driverWorkShift.weekdayEndHours}
                              onChange={handleChangeWeekdayEndHours}
                              defaultValue={undefined}
                              error={!!error.weekdayEndHours}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                !!driverWorkShift.weekdayIsDayOff &&
                                !!driverWorkShift.holidayIsDayOff
                              }
                            />
                            <Typography
                              sx={{ alignSelf: "center" }}
                              component={"span"}
                            >
                              &nbsp;:&nbsp;
                            </Typography>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingMins}
                              value={driverWorkShift.weekdayEndMinutes}
                              onChange={handleChangeWeekdayEndMinutes}
                              defaultValue={undefined}
                              error={!!error.weekdayEndMinutes}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                !!driverWorkShift.weekdayIsDayOff &&
                                !!driverWorkShift.holidayIsDayOff
                              }
                            />
                          </Grid>
                        </FormControl>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <FormControl>
                        <FormLabel component="legend" sx={{ fontSize: 1 }}>
                          {`${DriverWorkCyclesConstants.Holiday} : `}
                        </FormLabel>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                style={{ fontSize: "10px" }}
                                checked={sameWorkingTime}
                                onChange={handleChangeSameWorkingTime}
                                disabled={
                                  !!driverWorkShift.weekdayIsDayOff ||
                                  !!driverWorkShift.holidayIsDayOff
                                }
                              />
                            }
                            label={DriverWorkCyclesConstants.SameWorkingTime}
                          />
                        </FormGroup>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={sameWorkingTime ? undefined : "required"}
                    >
                      <Grid item>
                        <FormControl>
                          <FormLabel sx={{ fontSize: "0.75rem" }}>
                            {`${DriverWorkCyclesConstants.Holiday} : ${DriverWorkCyclesConstants.StartTime}`}
                          </FormLabel>
                          <Grid container item>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingHours}
                              value={driverWorkShift.holidayStartHours}
                              onChange={handleChangeHolidayStartHours}
                              defaultValue={undefined}
                              error={!!error.holidayStartHours}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                sameWorkingTime ||
                                (!!driverWorkShift.weekdayIsDayOff &&
                                  !!driverWorkShift.holidayIsDayOff)
                              }
                            />
                            <Typography
                              sx={{ alignSelf: "center" }}
                              component={"span"}
                            >
                              &nbsp;:&nbsp;
                            </Typography>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingMins}
                              value={driverWorkShift.holidayStartMinutes}
                              onChange={handleChangeHolidayStartMinutes}
                              defaultValue={undefined}
                              error={!!error.holidayStartMinutes}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                sameWorkingTime ||
                                (!!driverWorkShift.weekdayIsDayOff &&
                                  !!driverWorkShift.holidayIsDayOff)
                              }
                            />
                          </Grid>
                        </FormControl>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={sameWorkingTime ? undefined : "required"}
                    >
                      <Grid item>
                        <FormControl>
                          <FormLabel
                            sx={{ fontSize: "0.75rem", whiteSpace: "pre-line" }}
                          >
                            {`${DriverWorkCyclesConstants.Holiday} : ${DriverWorkCyclesConstants.EndTime}`}
                          </FormLabel>
                          <Grid container item>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingHours}
                              value={driverWorkShift.holidayEndHours}
                              onChange={handleChangeHolidayEndHours}
                              defaultValue={undefined}
                              error={!!error.holidayEndHours}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                sameWorkingTime ||
                                (!!driverWorkShift.weekdayIsDayOff &&
                                  !!driverWorkShift.holidayIsDayOff)
                              }
                            />
                            <Typography
                              sx={{ alignSelf: "center" }}
                              component={"span"}
                            >
                              &nbsp;:&nbsp;
                            </Typography>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingMins}
                              value={driverWorkShift.holidayEndMinutes}
                              onChange={handleChangeHolidayEndMinutes}
                              defaultValue={undefined}
                              error={!!error.holidayEndMinutes}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              disabled={
                                sameWorkingTime ||
                                (!!driverWorkShift.weekdayIsDayOff &&
                                  !!driverWorkShift.holidayIsDayOff)
                              }
                            />
                          </Grid>
                        </FormControl>
                      </Grid>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                loading={loading}
                forSave={!forEdit}
                onCancel={onCancel}
                onSubmit={handleSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
